import {useSettings} from '@common/core/settings/use-settings';
import {useAppearanceEditorMode} from '@common/admin/appearance/commands/use-appearance-editor-mode';
import {useAuth} from '@common/auth/use-auth';
import {Route, Routes, useLocation, useNavigate} from 'react-router-dom';
import {ToastContainer} from '@common/ui/toast/toast-container';
import {EmailVerificationPage} from '@common/auth/ui/email-verification-page/email-verification-page';
import {AppearanceListener} from '@common/admin/appearance/commands/appearance-listener';
import {CookieNotice} from '@common/ui/cookie-notice/cookie-notice';
import {AuthRoute} from '@common/auth/guards/auth-route';
import React, {Fragment, useEffect, useLayoutEffect} from 'react';
import {FullPageLoader} from '@common/ui/progress/full-page-loader';
import {DynamicHomepage} from '@common/ui/dynamic-homepage';
import {LandingPage} from '@app/landing-page/landing-page';
import {AuthRoutes} from '@common/auth/auth-routes';
import {BillingRoutes} from '@common/billing/billing-routes';
import {NotificationRoutes} from '@common/notifications/notification-routes';
import {ContactUsPage} from '@common/contact/contact-us-page';
import {CustomPageLayout} from '@common/custom-page/custom-page-layout';
import {NotFoundPage} from '@common/ui/not-found-page/not-found-page';
import {DialogStoreOutlet} from '@common/ui/overlays/store/dialog-store-outlet';
import {useSelectedLocale} from '@common/i18n/selected-locale';
// import './assets/scss/streamit.scss';
// import './assets/scss/custom.scss';
// import './assets/scss/rtl.scss';
// import './assets/custom/css/custom.scss';
import './i18n';
import DownloadGuidPage from './download-guid-page';
import { SitePageLayout } from './site-page-layout';

const AdminRoutes = React.lazy(() => import('@common/admin/admin-routes'));
const SwaggerApiDocs = React.lazy(
  () => import('@common/swagger/swagger-api-docs-page'),
);
const SiteRoutes = React.lazy(() => import('@app/site-routes'));

export function AppRoutes() {
  const {homepage, billing, notifications, require_email_confirmation, api} =
    useSettings();
  const {isAppearanceEditorActive} = useAppearanceEditorMode();
  const {user, hasPermission} = useAuth();
  const {locale} = useSelectedLocale();

  const location = useLocation();
  const navigate = useNavigate();

  const reloadPage = () => {
    navigate(window.location.pathname, {replace: true});
  };

  function loadStylesIfNotAdmin() {
    if (location.pathname.startsWith('/admin')) {
      // حذف استایل custom.scss
      const customLink = document.querySelector('link[href*="streamit.scss"]');

      if (customLink) {
        customLink.remove();
      }
      const customLink2 = document.querySelector('link[href*="custom.scss"]');
      if (customLink2) {
        customLink2.remove();
      }
      const customLink3 = document.querySelector('link[href*="rtl.scss"]');
      if (customLink3) {
        customLink3.remove();
      }
    }

    else {
      reloadPage();
      // وارد کردن مجدد استایل custom.scss
      import('./assets/scss/streamit.scss');
      import('./assets/scss/custom.scss');
      import('./assets/scss/rtl.scss');
    }
  }

  useEffect(() => {
    loadStylesIfNotAdmin();
  }, [location.pathname]);

  useLayoutEffect(() => {
    if (locale.language === 'fa') {
      document.body.style.direction = 'rtl';
      document.documentElement.dir = 'rtl';
    } else {
      document.body.style.direction = 'ltr';
      document.documentElement.dir = 'ltr';
    }
  }, []);

  if (user != null && require_email_confirmation && !user.email_verified_at) {
    return (
      <Fragment>
        <ToastContainer />
        <Routes>
          <Route path="*" element={<EmailVerificationPage />} />
        </Routes>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <AppearanceListener />
      <CookieNotice />
      <ToastContainer />
      <Routes>
        <Route
          path="/*"
          element={
            <AuthRoute requireLogin={false} permission="titles.view">
              <React.Suspense fallback={<FullPageLoader screen />}>
                <SiteRoutes />
              </React.Suspense>
            </AuthRoute>
          }
        />
        <Route
          path="download-guid"
          element={
            <AuthRoute requireLogin={true}>
              <React.Suspense fallback={<FullPageLoader screen />}>
              <SitePageLayout>
                <DownloadGuidPage />
              </SitePageLayout>
              </React.Suspense>
            </AuthRoute>
          }
        />
        {!homepage?.type?.startsWith('channel') &&
          (user == null || isAppearanceEditorActive) && (
            <Route
              path="/"
              element={
                <DynamicHomepage homepageResolver={() => <LandingPage />} />
              }
            />
          )}
        <Route
          path="/admin/*"
          element={
            <AuthRoute permission="admin.access">
              <React.Suspense fallback={<FullPageLoader screen />}>
                <AdminRoutes />
              </React.Suspense>
            </AuthRoute>
          }
        />
        {AuthRoutes}
        {billing.enable && BillingRoutes}
        {notifications.integrated && NotificationRoutes}
        {api?.integrated && hasPermission('api.access') && (
          <Route
            path="api-docs"
            element={
              <React.Suspense fallback={<FullPageLoader screen />}>
                <SwaggerApiDocs />
              </React.Suspense>
            }
          />
        )}
        <Route path="contact" element={<ContactUsPage />} />
        <Route path="pages/:pageSlug" element={<CustomPageLayout />} />
        <Route path="pages/:pageId/:pageSlug" element={<CustomPageLayout />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <DialogStoreOutlet />
    </Fragment>
  );
}
